import { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { GridLayout } from 'shared/ui/grid-layout';
import { OkvedGroupDto } from '@sr/dto';
import { Button, IconButton, TableRow, TableCell, Typography, Box, Stack } from '@mui/material';
import { useSnack } from 'shared/ui/snack';
import { ErrorBanner } from '../../../shared/ui/error-banner';
import { useDeleteOkvedGroup, useOkvedGroupList } from './okved.hooks';
import { okvedApi } from 'entities/okved/api-slice';
import { FormikErrors, FormikValues } from 'formik';
import { VALIDATION_REQUIRED_FIELD } from 'utils/form-tools';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { FormDialog } from 'shared/ui/form-dialog';
import OkvedGroupForm from './okved-group.form';
import useConfirmDialog from 'hooks/confirm-dialog.hook';

export interface FormValues {
  okvedId: number | null,
  code: string | '';
  name: string | '';
}

export const validate = (values: FormValues) => {
  const errors: FormikErrors<FormValues> = {};
  if (!values.code) errors.code = VALIDATION_REQUIRED_FIELD;
  if (!values.name) errors.name = VALIDATION_REQUIRED_FIELD;

  return errors;
};

const initialValues: FormValues = {
  okvedId: null,
  code: '',
  name: ''
};

interface OkvedGroupListProps {
  partitionId: number | null;
}

const columns = ['Группа', 'Наименование группы', ''];

const OkvedGroupList = ({ partitionId }: OkvedGroupListProps) => {
  const { collection, error, isLoading } = useOkvedGroupList(partitionId || 0);

  const [showAddDialog, setShowAddDialog] = useState(false);

  const { showSuccess, showError } = useSnack();
  const [addItem, { isError }] = okvedApi.useCreateOkvedGroupMutation();

  const confirmDialog = useConfirmDialog();
  
  const deleteOkvedPartition = useDeleteOkvedGroup();

  if (error) {
    return <ErrorBanner errorMessage={error} />;
  }

  const handleAddOkvedGroup = async (values: FormikValues) => {
    if (partitionId === null) {
      showError('Не выбран раздел ОКВЭД');
      return;
    }
    await addItem({
      code: values.code,
      name: values.name,
      okvedId: partitionId
    })
      .unwrap()
      .then(() => {
        showSuccess('Запись успешно добавлена');
        setShowAddDialog(false);
      })
      .catch((e) => showError(`Ошибка записи: ${extractNestJsErrorMessage(e)}`));
  };

      
  const handleDelete = (id: number) => {
    confirmDialog({ content: 'Удалить группу ОКВЭД?', title: 'Требуется подтверждение' })
      .then(() => {
        if (partitionId) {
          return deleteOkvedPartition(id, partitionId)
            .then(() => showSuccess('Группа ОКВЭД удалена'))
            .catch((e) => showError('Ошибка удаления группы ОКВЭД', extractNestJsErrorMessage(e)));
        }
      });
  };
    
  return (
    <Box>
      <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowAddDialog(true)} disabled={partitionId === null}>
                    Добавить
      </Button>
            
      <GridLayout<OkvedGroupDto>
        columns={columns}
        items={collection}
        isLoading={isLoading}
        error={error}
        itemRender={(okvedGroup) => (
          <Row
            key={okvedGroup.id}
            okved={okvedGroup}
            onDeleteClick={() => handleDelete(okvedGroup.id)}
          />
        )}
        noItemsText={partitionId ? 'Список групп пуст' : 'Выберите раздел для отображения групп'}
      />

      <FormDialog
        initialValues={initialValues}
        renderForm={() => (
          <Box minWidth={450} pb={1}>
            <Stack spacing={2} direction="column">
              <OkvedGroupForm />
            </Stack>
          </Box>
        )}
        renderTitle="Новая раздел ОКВЭД"
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSubmit={handleAddOkvedGroup}
        validate={validate}
        dialogProps={{
          fullWidth: true
        }}
      />
    </Box>
  );
};

type Props = {
  okved: OkvedGroupDto;
  onDeleteClick: () => void;
};

const Row = ({ okved, onDeleteClick }: Props) => {
  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2">
          {okved.code}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {okved.name}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <IconButton edge="end" onClick={onDeleteClick}>
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export default OkvedGroupList;
